// src/pages/AllCoursesPage.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Breadcrumbs,
  Link as MuiLink, // Alias Link to avoid conflict with react-router-dom Link
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import { useTheme } from '@mui/material/styles';
import CourseCard from './component/CourseCard';

// Dummy data for courses
const allCourses = [
  {
    id: 1,
    title: 'AI in the Classroom: First Steps',
    description: 'Learn the fundamentals of integrating Artificial Intelligence tools into your teaching practices.',
    progress: 80,
    status: 'in-progress',
    category: 'AI & Tech',
  },
  {
    id: 2,
    title: 'Project-Based Learning with Tech Tools',
    description: 'Design and implement engaging project-based learning experiences using modern technology.',
    progress: 30,
    status: 'in-progress',
    category: 'Pedagogy',
  },
  {
    id: 3,
    title: 'Advanced Data Science for Educators',
    description: 'Deep dive into data analysis and interpretation relevant for educational research.',
    progress: 0,
    status: 'not-started',
    category: 'Data Science',
  },
  {
    id: 4,
    title: 'Gamification in Education',
    description: 'Explore strategies to apply game design elements and game principles in learning contexts.',
    progress: 100,
    status: 'completed',
    category: 'Pedagogy',
  },
  {
    id: 5,
    title: 'Understanding Blockchain for K-12',
    description: 'An introductory course on blockchain technology and its potential applications in school administration and learning.',
    progress: 0,
    status: 'not-started',
    category: 'Emerging Tech',
  },
  {
    id: 6,
    title: 'Effective Online Teaching Strategies',
    description: 'Master the best practices for delivering high-quality education in a virtual environment.',
    progress: 60,
    status: 'in-progress',
    category: 'Pedagogy',
  },
  {
    id: 7,
    title: 'Robotics for Elementary School Teachers',
    description: 'Hands-on training for integrating simple robotics projects into elementary school curriculum.',
    progress: 0,
    status: 'not-started',
    category: 'STEM',
  },
  {
    id: 8,
    title: 'Cybersecurity Fundamentals for Educators',
    description: 'Understand the basics of cybersecurity to protect your data and your students online.',
    progress: 100,
    status: 'completed',
    category: 'IT Security',
  },
];

const itemsPerPage = 6; // Number of courses per page

const AllCoursesPage = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // const filteredCourses = allCourses.filter((course) => {
  //   const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase());
  //   const matchesStatus = statusFilter === 'all' || course.status === statusFilter;
  //   const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
  //   return matchesSearch && matchesStatus && matchesCategory;
  // });

  const filteredCourses: any[] = [];

  const pageCount = Math.ceil(filteredCourses.length / itemsPerPage);
  const displayedCourses = filteredCourses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (event: any, value: any) => {
    setCurrentPage(value);
  };

  const uniqueCategories = ['all', ...new Set(allCourses.map(course => course.category))];

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2, // Adjust padding for sidebar and header
          width: '100%',
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
            <Typography variant="h6" style={{color: "rgb(15, 105, 185)"}}>My Courses</Typography>
        </Box>

        <Paper
          sx={{
            p: 3,
            mb: 4,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[1],
            bgcolor: 'background.paper',
            display: 'flex',
            flexWrap: 'wrap', // Allow wrapping on smaller screens
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            label="Search Courses"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200, flexGrow: 1 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="in-progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="not-started">Not Started</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="category-filter-label">Category</InputLabel>
            <Select
              labelId="category-filter-label"
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {uniqueCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        <Grid container spacing={3}>
          {displayedCourses.length > 0 ? (
            displayedCourses.map((course) => (
              <Grid item xs={12} sm={6} md={4} key={course.id}>
                <CourseCard
                  title={course.title}
                  description={course.description}
                  progress={course.progress}
                  status={course.status}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, textAlign: 'center', boxShadow: theme.shadows[1] }}>
                <Typography variant="h6" color="text.secondary">
                  No data available.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {pageCount > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
            <Pagination
              count={pageCount}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AllCoursesPage;